import { useState } from 'react'
import { useNotificationsState } from '@/state/notifications'
import type { Notification, NotificationType } from '@/models/notification'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/ui/loading'
import { Bell, ShoppingCart, Users, Info, Check, CheckCheck } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

const NOTIFICATION_ICONS: Record<NotificationType, typeof Bell> = {
  purchase: ShoppingCart,
  referral: Users,
  system: Info
}

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  purchase: 'bg-blue-100 text-blue-800',
  referral: 'bg-green-100 text-green-800',
  system: 'bg-purple-100 text-purple-800'
}

const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  purchase: 'Purchase',
  referral: 'Referral',
  system: 'System'
}

function formatRelativeTime(dateString: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return t('common.time.justNow')
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return t('common.time.minutesAgo', { count: minutes })
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return t('common.time.hoursAgo', { count: hours })
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400)
    return t('common.time.daysAgo', { count: days })
  } else {
    const weeks = Math.floor(diffInSeconds / 604800)
    return t('common.time.weeksAgo', { count: weeks })
  }
}

export default function NotificationsPage() {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<{ type?: NotificationType; unread_only?: boolean }>({})
  
  const {
    notifications,
    notificationsMeta,
    unreadCount,
    dataIsLoading,
    dataError,
    markAsRead,
    markAllAsRead,
    markAsReadLoading,
    markAllAsReadLoading
  } = useNotificationsState()

  const handleTypeChange = (type: string) => {
    setFilter((prev) => ({
      ...prev,
      type: type === 'all' ? undefined : (type as NotificationType)
    }))
  }

  const handleUnreadOnlyChange = (value: string) => {
    setFilter((prev) => ({
      ...prev,
      unread_only: value === 'unread' ? true : undefined
    }))
  }

  const handleMarkAsRead = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
  }

  const handleMarkAllAsRead = () => {
    markAllAsRead(undefined)
  }

  const filteredNotifications = notifications.filter((notification) => {
    if (filter.type && notification.type !== filter.type) return false
    if (filter.unread_only && notification.is_read) return false
    return true
  })

  if (dataError) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {t('notifications.loadError')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="w-full md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t('notifications.title')}</h1>
          <p className="text-muted-foreground">
            {t('notifications.subtitle')}
          </p>
        </div>
        
        {unreadCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadLoading}
              className="flex items-center gap-2"
            >
              <CheckCheck className="w-4 h-4" />
              {t('notifications.markAllAsRead')}
            </Button>
          </motion.div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('notifications.totalNotifications')}</p>
                <p className="text-2xl font-bold">{notificationsMeta?.total || 0}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Bell className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('notifications.unreadNotifications')}</p>
                <p className="text-2xl font-bold text-destructive">{unreadCount}</p>
              </div>
              <div className="p-3 bg-destructive/10 rounded-full">
                <Bell className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('notifications.readNotifications')}</p>
                <p className="text-2xl font-bold text-green-600">
                  {(notificationsMeta?.total || 0) - unreadCount}
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-full">
                <Check className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.filters')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">{t('notifications.type')}</label>
              <Select onValueChange={handleTypeChange} defaultValue="all">
                <SelectTrigger>
                  <SelectValue placeholder={t('notifications.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('notifications.allTypes')}</SelectItem>
                  <SelectItem value="purchase">{t('notifications.types.purchase')}</SelectItem>
                  <SelectItem value="referral">{t('notifications.types.referral')}</SelectItem>
                  <SelectItem value="system">{t('notifications.types.system')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">{t('notifications.status')}</label>
              <Select onValueChange={handleUnreadOnlyChange} defaultValue="all">
                <SelectTrigger>
                  <SelectValue placeholder={t('notifications.allStatuses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('notifications.allStatuses')}</SelectItem>
                  <SelectItem value="unread">{t('notifications.unreadOnly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('notifications.notificationHistory')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('notifications.showingNotifications', { count: filteredNotifications.length, total: notificationsMeta?.total || 0 })}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {dataIsLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg text-muted-foreground">{t('notifications.noNotifications')}</p>
              <p className="text-sm text-muted-foreground/70 mt-1">{t('notifications.noNotificationsDescription')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type]
                const colorClass = NOTIFICATION_COLORS[notification.type]

                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      'flex items-start gap-4 p-4 rounded-xl border transition-colors',
                      !notification.is_read 
                        ? 'bg-primary/5 border-primary/20' 
                        : 'bg-card border-border hover:bg-accent/50'
                    )}
                  >
                    <div className={cn(
                      'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                      colorClass
                    )}>
                      <Icon size={20} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className={cn(
                            'font-medium',
                            !notification.is_read && 'text-foreground'
                          )}>
                            {notification.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            {notification.message}
                          </p>
                        </div>
                        <Badge variant="secondary" className={colorClass}>
                          {NOTIFICATION_LABELS[notification.type]}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(notification.created_at, t)}
                        </span>
                        
                        {!notification.is_read ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkAsRead(notification)}
                            disabled={markAsReadLoading}
                            className="h-7 text-xs"
                          >
                            <Check size={14} className="mr-1" />
                            {t('notifications.markAsRead')}
                          </Button>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground/70">
                            <Check size={14} />
                            {t('notifications.read')}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
